import { Box, Typography } from '@mui/material';
import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import { setActiveHistoryItem } from 'store/ticket-service/ticketSlice';
import { ITicketChange } from 'store/ticket-service/types';
import { getLoadedUsers } from 'store/user-service/selector';

import { HistoryActionTypes, NamespacesList, SubscriptionLevels } from 'core/enums/common';

import { CustomAvatar } from 'components/Header/CustomAvatar/CustomAvatar';

import { ReactComponent as CreatedIcon } from 'assets/icons/create-request.svg';
import { ReactComponent as EditedIcon } from 'assets/icons/edit-request.svg';
import { ReactComponent as ApprovedIcon } from 'assets/icons/success.svg';
import { titleTextColor } from 'assets/styles/_palette';

interface IHistoryItemProps {
	historyItem: ITicketChange;
	isFirstChange: boolean;
}

export interface IUserData {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	avatarLink?: string;
	accountPlan: SubscriptionLevels;
}

export const HistoryItem: FC<IHistoryItemProps> = ({ historyItem, isFirstChange }) => {
	const { t: changesVersioningTranslate } = useTranslation(NamespacesList['changesVersioning']);

	const dispatch = useAppDispatch();
	const loadedUsers = useSelector(getLoadedUsers);

	const userData: IUserData = useMemo(() => {
		try {
			return JSON.parse(historyItem.userEmail);
		} catch (err) {
			return {
				email: historyItem.userEmail,
				firstName: null,
				lastName: null,
			};
		}
	}, [historyItem]);

	const changeAuthor = useMemo(() => {
		const historyItemAuthorId = JSON.parse(historyItem?.userEmail || '{}')?.id;
		return loadedUsers.find((user) => user.id === historyItemAuthorId);
	}, [loadedUsers, historyItem]);

	const getActionTimeYearString = (isoString: string): string => {
		const time = new Date(isoString);

		return time.toLocaleDateString(['en-GB', 'en-US'], {
			year: 'numeric',
		});
	};

	const getActionTimeMonthDayString = (isoString: string): string => {
		const time = new Date(isoString);

		return time.toLocaleDateString(['en-GB', 'en-US'], {
			day: '2-digit',
			month: 'short',
		});
	};

	const getActionTimeHourString = (isoString: string): string => {
		const time = new Date(isoString);

		const hour = time.getUTCHours();
		const minute = time.getUTCMinutes();

		return `${hour}:${minute}`;
	};

	const getUserDataBlock = (userData: IUserData) => {
		const displayFullName = userData?.firstName && userData?.lastName;

		return displayFullName ? (
			<Box sx={{ display: 'flex', gap: '8px' }}>
				<CustomAvatar
					firstName={userData.firstName}
					lastName={userData.lastName}
					avatarLink={userData?.avatarLink}
					stylesObj={{ width: '20px', height: '20px', fontSize: '12px' }}
				/>
				<Typography
					sx={{
						color: '#707A8E',
						fontSize: '14px',
					}}
				>
					{userData.firstName || ''} {userData.lastName}
				</Typography>
			</Box>
		) : (
			<Typography
				sx={{
					color: '#707A8E',
					fontSize: '14px',
				}}
			>
				{userData?.email || ''}
			</Typography>
		);
	};

	const getChangeIcons = (actionType: HistoryActionTypes) => {
		switch (actionType) {
			case HistoryActionTypes.ticketCreate:
				return <CreatedIcon style={{ width: '24px', height: '24px' }} />;
			case HistoryActionTypes.ticketApprove:
				return <ApprovedIcon style={{ width: '24px', height: '24px' }} />;
			case HistoryActionTypes.userStoriesEdit:
			case HistoryActionTypes.useCasesEdit:
			case HistoryActionTypes.notificationsEdit:
				return (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<EditedIcon style={{ width: '24px', height: '24px' }} />
					</Box>
				);
			default:
				return <EditedIcon style={{ width: '24px', height: '24px' }} />;
		}
	};

	const isHistoryItemClickable = useMemo(() => {
		return (
			historyItem.actionHistoryEnum === HistoryActionTypes.userStoriesEdit ||
			historyItem.actionHistoryEnum === HistoryActionTypes.useCasesEdit ||
			historyItem.actionHistoryEnum === HistoryActionTypes.testCasesEdit ||
			historyItem.actionHistoryEnum === HistoryActionTypes.notificationsEdit
		);
	}, [historyItem]);

	const handleHistoryItemClick = () => {
		if (isHistoryItemClickable) {
			dispatch(setActiveHistoryItem(historyItem));
		}
	};

	return (
		<Box
			sx={{
				position: 'relative',
				display: 'flex',
				flexDirection: 'column',
				gap: '10px',
				minWidth: '220px',
				margin: '0 0 0 12px',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: '8px',
					cursor: isHistoryItemClickable ? 'pointer' : 'default',
					pointerEvents: historyItem.newValue && historyItem.oldValue ? 'auto' : 'none',
					'&::before': {
						content: isFirstChange ? undefined : '""',
						position: 'absolute',
						top: '18px',
						left: '-16.5px',
						height: '77px',
						border: '1.5px solid #9FA6B3',
					},
				}}
				onClick={handleHistoryItemClick}
			>
				{getChangeIcons(historyItem.actionHistoryEnum)}

				<Typography sx={{ color: titleTextColor, fontWeight: 700 }}>
					{changesVersioningTranslate(
						`statuses.${HistoryActionTypes[historyItem.actionHistoryEnum]}`
					)}
				</Typography>
			</Box>
			<Box>
				<Typography
					sx={{
						color: titleTextColor,
						fontSize: '14px',
						'&::before': {
							content: '""',
							position: 'absolute',
							top: '3px',
							left: '-24px',
							width: '13px',
							height: '13px',
							border: '2px solid #9FA6B3',
							borderRadius: '50%',
						},
					}}
				>
					{getActionTimeMonthDayString(historyItem.actionTime)},{' '}
					{getActionTimeYearString(historyItem.actionTime)}{' '}
					{getActionTimeHourString(historyItem.actionTime)}
				</Typography>
				{getUserDataBlock(changeAuthor || userData)}
			</Box>
		</Box>
	);
};
